import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Prospect } from '../../types';

interface ProspectSelectProps {
  prospects: Prospect[];
  value: string;
  onChange: (prospectId: string) => void;
  error?: string;
  placeholder?: string;
}

export const ProspectSelect: React.FC<ProspectSelectProps> = ({
  prospects,
  value,
  onChange,
  error,
  placeholder = 'Seleccione un prospecto',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedProspect = prospects.find((p) => p.id === value);

  const filteredProspects = prospects.filter((prospect) => {
    const search = searchTerm.toLowerCase();
    return (
      prospect.name.toLowerCase().includes(search) ||
      prospect.email.toLowerCase().includes(search) ||
      prospect.propertyName?.toLowerCase().includes(search) ||
      prospect.phone.toLowerCase().includes(search)
    );
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (prospectId: string) => {
    onChange(prospectId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón principal */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-left flex items-center justify-between ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${selectedProspect ? 'pr-8' : ''}`}
      >
        <span className={selectedProspect ? 'text-gray-900' : 'text-gray-400'}>
          {selectedProspect
            ? `${selectedProspect.name} - ${selectedProspect.email}${selectedProspect.propertyName ? ` (${selectedProspect.propertyName})` : ''}`
            : placeholder}
        </span>
        <div className="flex items-center space-x-2">
          {selectedProspect && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Buscador */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar prospecto..."
                className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Lista de prospectos */}
          <div className="overflow-y-auto max-h-48">
            {filteredProspects.length > 0 ? (
              filteredProspects.map((prospect) => (
                <button
                  key={prospect.id}
                  type="button"
                  onClick={() => handleSelect(prospect.id)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                    value === prospect.id ? 'bg-primary/10 border-l-4 border-primary' : ''
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{prospect.name}</span>
                    <span className="text-xs text-gray-500 mt-0.5">{prospect.email}</span>
                    {prospect.propertyName && (
                      <span className="text-xs text-gray-400 mt-0.5">
                        Propiedad: {prospect.propertyName}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 mt-0.5">{prospect.phone}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No se encontraron prospectos
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

